/*!
 * 监测后台 mock 数据
 * 后续接入后端 /v1/monitor/dashboard 后，用 service 层替换即可，组件无需改动
 */

import { RouteConfig } from 'vue-router'

/** 仪表盘 Hero 数据 */
export interface IMockHero {
    /** 英文小标题（顶部） */
    en: string
    /** 主标题 */
    title: string
    /** 状态点文字 */
    statusText: string
    /** 状态判定条件说明 */
    judgeText: string
    /** 数据源行（html 字符串，<b> 为强调） */
    srcPrimary: string
    /** 第二行说明 */
    srcSecondary: string
}

export const MOCK_HERO: IMockHero = {
    en: 'REALTIME OPERATIONS',
    title: '实时运行态势',
    statusText: '系统状态 · 正常',
    judgeText: '判定：慢接口 ≤ 50 且 违规增量 ≤ 50',
    srcPrimary: '<b>数据源</b> api_stats_hourly · page_render_stats_hourly · user_rate_violation_log',
    srcSecondary: '已排除 OPTIONS 预检 · 明细保留 30 天 · 指标卡折线 = 近 7 日走势'
}

/** 6 项指标卡 */
export interface IMockMetric {
    id: string
    label: string
    /** 主数值 */
    value: number
    /** 小数位（默认 0） */
    decimals?: number
    /** 后缀（如 %） */
    suffix?: string
    /** 是否千分位分组 */
    group?: boolean
    /** 环比值（带符号方向） */
    delta: number
    /** 环比单位（% 或 ''） */
    deltaUnit: '%' | ''
    /** 环比文字 */
    deltaText: string
    /** 环比色调：up 红 / down 绿 / warn 琥珀 */
    deltaTone: 'up' | 'down' | 'warn'
    /** FOCUS 顶条 */
    focus?: boolean
    /** 7 日走势 */
    spark: number[]
    /** 点击跳转目标 */
    navTarget: string
    /** 跳转携带的 filter 信息（用于展示 toast，可选） */
    navFilter?: string
}

export const MOCK_METRICS: IMockMetric[] = [
    {
        id: 'request_total',
        label: '请求总量',
        value: 1284930,
        group: true,
        delta: 6.2,
        deltaUnit: '%',
        deltaText: '较昨日',
        deltaTone: 'up',
        spark: [62, 58, 65, 70, 66, 74, 78],
        navTarget: '/monitor/api',
        navFilter: '全部'
    },
    {
        id: 'ssr_ratio',
        label: 'SSR 占比',
        value: 71.4,
        decimals: 1,
        suffix: '%',
        delta: -1.1,
        deltaUnit: '%',
        deltaText: '较昨日',
        deltaTone: 'down',
        spark: [74, 73, 75, 72, 73, 71.8, 71.4],
        navTarget: '/monitor/api',
        navFilter: 'source = ssr'
    },
    {
        id: 'bot_ratio',
        label: '爬虫占比',
        value: 18.9,
        decimals: 1,
        suffix: '%',
        delta: 3.7,
        deltaUnit: '%',
        deltaText: '较昨日',
        deltaTone: 'up',
        spark: [12, 14, 13, 16, 15, 17.5, 18.9],
        navTarget: '/monitor/api',
        navFilter: 'visitor_type = bot'
    },
    {
        id: 'bot_ssr_pressure',
        label: '爬虫 × SSR 压力',
        value: 172640,
        group: true,
        delta: 9.5,
        deltaUnit: '%',
        deltaText: '缓存收益上限',
        deltaTone: 'warn',
        focus: true,
        spark: [9, 11, 10, 14, 13, 16, 17.3],
        navTarget: '/monitor/api',
        navFilter: 'source = ssr AND visitor_type = bot'
    },
    {
        id: 'slow_api_count',
        label: '慢接口数',
        value: 23,
        delta: 4,
        deltaUnit: '',
        deltaText: '较昨日',
        deltaTone: 'up',
        spark: [14, 12, 16, 18, 17, 21, 23],
        navTarget: '/monitor/api',
        navFilter: 'avg_ms > 1000'
    },
    {
        id: 'rate_violation_users',
        label: '频率违规用户数',
        value: 142,
        delta: 12,
        deltaUnit: '',
        deltaText: '较昨日',
        deltaTone: 'up',
        spark: [98, 110, 105, 120, 128, 136, 142],
        navTarget: '/monitor/risk',
        navFilter: '频率违规 Tab'
    }
]

/* === 02 前十接口 === */
export interface IMockTopApi {
    rank: number
    path: string
    calls: number
    avgMs: number
    /** 7 日柱状百分比（18~80） */
    spark: number[]
}

export const MOCK_TOP_API: IMockTopApi[] = [
    { rank: 1,  path: '/v1/reporter',      calls: 382114, avgMs: 412, spark: [42, 48, 52, 58, 62, 68, 74] },
    { rank: 2,  path: '/v1/news/list',     calls: 301889, avgMs: 188, spark: [38, 42, 40, 48, 52, 58, 62] },
    { rank: 3,  path: '/v1/user/profile',  calls: 268402, avgMs: 96,  spark: [40, 42, 48, 44, 50, 52, 56] },
    { rank: 4,  path: '/v1/message/types', calls: 199330, avgMs: 143, spark: [32, 36, 40, 38, 44, 48, 52] },
    { rank: 5,  path: '/v1/comment/list',  calls: 154771, avgMs: 520, spark: [28, 32, 36, 34, 40, 44, 50] },
    { rank: 6,  path: '/v1/search',        calls: 142008, avgMs: 233, spark: [30, 32, 28, 36, 34, 40, 42] },
    { rank: 7,  path: '/v1/reporter/enc',  calls: 121556, avgMs: 310, spark: [22, 26, 30, 32, 34, 38, 40] },
    { rank: 8,  path: '/v1/live/list',     calls: 98442,  avgMs: 201, spark: [20, 24, 26, 22, 30, 28, 34] },
    { rank: 9,  path: '/v1/column',        calls: 87910,  avgMs: 176, spark: [18, 22, 20, 24, 22, 28, 30] },
    { rank: 10, path: '/v1/ad/slot',       calls: 76038,  avgMs: 88,  spark: [16, 20, 18, 22, 20, 26, 24] }
]

/* === 03 前十重负载页面 === */
export interface IMockTopPage {
    rank: number
    route: string
    renders: number
    avgApi: number
    /** 压力指数 = renders × avgApi */
    pressure: number
}

export const MOCK_TOP_PAGE: IMockTopPage[] = [
    { rank: 1,  route: '/reporter/enc:abc12', renders: 9210, avgApi: 11.2, pressure: 103152 },
    { rank: 2,  route: '/news/detail/:id',    renders: 7640, avgApi: 9.8,  pressure: 74872  },
    { rank: 3,  route: '/column/:cid',        renders: 6118, avgApi: 8.1,  pressure: 49556  },
    { rank: 4,  route: '/live/room/:rid',     renders: 5402, avgApi: 7.6,  pressure: 41055  },
    { rank: 5,  route: '/search/result',      renders: 4980, avgApi: 9.0,  pressure: 44820  },
    { rank: 6,  route: '/user/home',          renders: 4210, avgApi: 6.4,  pressure: 26944  },
    { rank: 7,  route: '/topic/:tid',         renders: 3870, avgApi: 7.1,  pressure: 27477  },
    { rank: 8,  route: '/video/play/:vid',    renders: 3440, avgApi: 8.3,  pressure: 28552  },
    { rank: 9,  route: '/rank/list',          renders: 2990, avgApi: 5.9,  pressure: 17641  },
    { rank: 10, route: '/mine/collect',       renders: 2510, avgApi: 5.2,  pressure: 13052  }
]

/* === 04 前十频率违规用户 === */
export type ViolationStatus = 'ban' | 'ok' | 'watch'

export interface IMockViolationUser {
    userId: string
    triggers: number
    /** 峰值/分：≥50 红，≥30 琥珀，其余默认 */
    peak: number
    status: ViolationStatus
}

const STATUS_TEXT: Record< ViolationStatus, string > = {
    ban: '已封禁',
    ok: '正常',
    watch: '观察'
}

export const MOCK_TOP_USERS: Array<IMockViolationUser & { statusText: string }> = [
    { userId: 'u_8842', triggers: 312, peak: 58, status: 'ban',   statusText: STATUS_TEXT.ban   },
    { userId: 'u_7710', triggers: 287, peak: 49, status: 'ban',   statusText: STATUS_TEXT.ban   },
    { userId: 'u_6603', triggers: 233, peak: 41, status: 'ok',    statusText: STATUS_TEXT.ok    },
    { userId: 'u_5521', triggers: 198, peak: 37, status: 'ban',   statusText: STATUS_TEXT.ban   },
    { userId: 'u_4490', triggers: 176, peak: 33, status: 'ok',    statusText: STATUS_TEXT.ok    },
    { userId: 'u_3387', triggers: 155, peak: 29, status: 'watch', statusText: STATUS_TEXT.watch },
    { userId: 'u_2204', triggers: 141, peak: 26, status: 'ok',    statusText: STATUS_TEXT.ok    },
    { userId: 'u_1982', triggers: 129, peak: 24, status: 'ban',   statusText: STATUS_TEXT.ban   },
    { userId: 'u_1755', triggers: 118, peak: 22, status: 'ok',    statusText: STATUS_TEXT.ok    },
    { userId: 'u_1633', triggers: 107, peak: 19, status: 'watch', statusText: STATUS_TEXT.watch }
]

/* === 05 模块1 实现对照 === */
export type Map1Tag = 'ok' | 'part' | 'later' | 'partial'

export interface IMockModule1Map {
    element: string
    tagClass: Map1Tag
    tagText: string
    desc: string
}

export const MOCK_MODULE1_MAP: IMockModule1Map[] = [
    { element: '6 项核心指标',         tagClass: 'ok',      tagText: '模块1已实现',  desc: 'api_stats_hourly 聚合表，N4 每小时生成' },
    { element: '24H 分布图',           tagClass: 'ok',      tagText: '模块1已实现',  desc: '按 stat_hour 取 24 行聚合数据绘制' },
    { element: '前十接口 / 页面',      tagClass: 'ok',      tagText: '模块1已实现',  desc: '聚合表已含 path / page_route / call_count / avg_ms' },
    { element: '频率违规用户',         tagClass: 'part',    tagText: '依赖现有表',  desc: '直读 user_rate_violation_log，庖丁端只读' },
    { element: '实时事件 / 告警',      tagClass: 'later',   tagText: '不在模块1',   desc: '当前为示意 mock；待模块三 / 告警系统接入' },
    { element: '指标卡 7 日折线',      tagClass: 'partial', tagText: '部分实现',    desc: '需按日保存 7 天聚合历史；当前聚合表仅最终值，可加表或按日期扩展' },
    { element: '接口 / 页面详情',      tagClass: 'ok',      tagText: '模块1已实现',  desc: '下钻 ssr_api_log 明细，request_id / visit_id 已聚簇' },
    { element: '用户详情画像',         tagClass: 'part',    tagText: '依赖现有表',  desc: '聚合 + 风控三表联查，模块1 不新建用户表' }
]

/** 抑制未使用变量警告 */
export type _RouteConfig = RouteConfig